/* eslint-disable */

/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form-main');
      this.optionSelectors = this.querySelectorAll('.option-selector');
      this.data = this.getProductData();
      this.variant = this.getSelectedVariant();
      this.selectedOptions = this.getSelectedOptions();
      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

      this.addEventListener('change', this.handleVariantChange.bind(this));

      // Listen for quantity changes to update max message
      const quantityInput = this.productForm?.querySelector('.qty-input__input[name="quantity"]');
      if (quantityInput) {
        quantityInput.addEventListener('change', () => {
          this.updateQuantityMax();
        });
        quantityInput.addEventListener('input', () => {
          this.updateQuantityMax();
        });
      }

      // Defer these to allow for quick-add modal to full initialize
      setTimeout(() => {
        this.updateAvailability();
        this.updateAddToCartButton();
        this.updateQuantityMax();
      });
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();
      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

      if (this.variant) {
        this.updateVariantInput(); // Must run first — form needs correct variant ID before anything else
        this.updateUrl(evt);
        try { this.updateMedia(); } catch (e) { console.warn('[VariantPicker] updateMedia error (ignored):', e.message); }
      }

      this.updateAddToCartButton();
      this.updateAvailability();
      this.updatePrice();
      this.updateWeight();
      this.updateBarcode();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      this.updateQuantityMax();
      this.updateMetafieldVisibility();
      VariantPicker.updateLabelText(evt);

      if (!this.preSelection) {
        this.dispatchEvent(new CustomEvent('on:variant:change', {
          bubbles: true,
          detail: {
            form: this.productForm,
            variant: this.variant,
            product: this.data.product
          }
        }));
      }
    }

    /**
     * Shows/hides variant metafield elements on the page as necessary
     */
    updateMetafieldVisibility() {
      document.querySelectorAll('[data-variant-metafield]').forEach((elem) => {
        elem.classList.toggle('hidden', elem.dataset.variantMetafield !== this.variant?.id.toString());
      });
    }

    /**
     * Admissibilite + delai de la variante courante, depuis la carte rendue par
     * snippets/backorder-data.liquid. Retourne null si la carte est absente.
     */
    getBackorderInfo() {
      if (this._boMap === undefined) {
        const el = this.section.querySelector('[data-backorder-map]');
        try { this._boMap = el ? JSON.parse(el.textContent) : null; }
        catch (e) { this._boMap = null; }
      }
      if (!this._boMap || !this.variant) return null;
      return this._boMap[String(this.variant.id)] || null;
    }

    /**
     * Affiche le message de delai UNIQUEMENT quand la variante courante est a la fois en
     * rupture et admissible, avec le texte propre a cette variante.
     */
    updateBackorderNotice(backorder, isOutOfStock) {
      // Rudy A. 2026-10-09 : le texte est recalculé à partir d'AUJOURD'HUI (jamais le texte
      // figé au rendu, qui peut venir d'un cache), et la propriété de ligne « Rupture de
      // stock » suit la variante : posée en rupture, retirée (désactivée) en stock.
      const show = !!(backorder && backorder.eligible && isOutOfStock && (backorder.delay || backorder.base));
      const text = show ? VariantPicker.backorderText(backorder, this.getBackorderLocale()) : '';
      this._boShow = show;
      this._boInfo = backorder;

      const wrap = this.section.querySelector('.backorder-delay-wrap');
      if (wrap) {
        wrap.hidden = !show;
        const textEl = wrap.querySelector('.backorder-delay__text');
        if (show && textEl) textEl.textContent = text;
      }

      const form = this.productForm || this.section.querySelector('.js-product-form-main');
      const prop = form && form.querySelector('[data-backorder-property]');
      if (prop) {
        prop.disabled = !show;
        prop.value = show ? text : '';
      }
      this.bindBackorderSubmit(form);
    }

    /** Langue de la carte de délais (attribut data-locale du script JSON). */
    getBackorderLocale() {
      const el = this.section.querySelector('[data-backorder-map]');
      return (el && el.dataset.locale) || document.documentElement.lang || 'fr';
    }

    /**
     * À l'ajout au panier, la propriété reçoit les dates calculées CE jour-là (page restée
     * ouverte depuis la veille comprise). Écoute en capture sur document : passe avant
     * product-form.js, qui construit son FormData plus tard.
     */
    bindBackorderSubmit(form) {
      if (!form || form.dataset.boSubmitBound) return;
      form.dataset.boSubmitBound = 'true';
      document.addEventListener('submit', (evt) => {
        if (evt.target !== form || !this._boShow) return;
        const prop = form.querySelector('[data-backorder-property]');
        if (prop) {
          prop.disabled = false;
          prop.value = VariantPicker.backorderText(this._boInfo, this.getBackorderLocale());
        }
      }, true);
    }

    /**
     * Message de délai calculé à partir d'aujourd'hui.
     * - from/to > 0 : fenêtre de livraison en dates (aujourd'hui + from → aujourd'hui + to jours) ;
     * - fin de fenêtre déjà passée (ne devrait pas arriver) : délai seul, jamais une date passée ;
     * - texte libre (from = 0) : texte tel quel.
     * @param {object} bo - Entrée de la carte : { delay, from, to, base }.
     * @param {string} locale - 'fr' | 'en'.
     * @param {Date} [now] - Pour les tests.
     */
    static backorderText(bo, locale, now) {
      if (!bo) return '';
      const en = String(locale || '').toLowerCase().startsWith('en');
      const from = Number(bo.from) || 0;
      const to = Number(bo.to) || 0;
      const base = (bo.base || '').trim();
      const today = now ? new Date(now) : new Date();
      today.setHours(0, 0, 0, 0);
      const delayOnly = () => {
        if (!base) return bo.delay || '';
        return en ? `Lead time: ${base}` : `Délai : ${base}`;
      };
      if ((from !== 0 || to !== 0) && !(from > 0 && to >= from)) return delayOnly();
      if (!(from > 0 && to >= from)) {
        // Pas de fenêtre calculable : texte libre tel quel ; un ancien texte daté est remplacé
        // par le délai seul.
        return /\d{1,2} (janvier|février|mars|avril|mai|juin|juillet|août|septembre|octobre|novembre|décembre|January|February|March|April|May|June|July|August|September|October|November|December)/.test(bo.delay || '')
          ? delayOnly() : (bo.delay || base);
      }
      const d1 = new Date(today); d1.setDate(d1.getDate() + from);
      const d2 = new Date(today); d2.setDate(d2.getDate() + to);
      if (d2 < today) return delayOnly();
      const fr = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
      const enM = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      const sameMonth = d1.getMonth() === d2.getMonth();
      if (en) {
        return sameMonth
          ? `This size is out of stock — expected delivery between ${enM[d1.getMonth()]} ${d1.getDate()} and ${d2.getDate()}.`
          : `This size is out of stock — expected delivery between ${enM[d1.getMonth()]} ${d1.getDate()} and ${enM[d2.getMonth()]} ${d2.getDate()}.`;
      }
      return sameMonth
        ? `Cette taille est en rupture de stock — livraison prévue entre le ${d1.getDate()} et le ${d2.getDate()} ${fr[d2.getMonth()]}.`
        : `Cette taille est en rupture de stock — livraison prévue entre le ${d1.getDate()} ${fr[d1.getMonth()]} et le ${d2.getDate()} ${fr[d2.getMonth()]}.`;
    }

    /**
     * Is the current customer B2B? Read from the picker's own data-b2b attribute
     * (set in Liquid: tag "b2b" or company contact), with the product form's
     * hidden input as fallback for templates that don't render the attribute.
     */
    isB2BCustomer() {
      if (this.dataset.b2b === 'true') return true;
      const form = this.productForm || (this.section && this.section.querySelector('.js-product-form-main'));
      const b2bInput = form && form.querySelector('[data-user-has-b2b-tag]');
      return !!(b2bInput && b2bInput.dataset.userHasB2bTag === 'true');
    }

    /**
     * Updates the "Add to Cart" button label and disabled state.
     */
    updateAddToCartButton() {
      this.productForm = this.section.querySelector('.js-product-form-main');
      if (!this.productForm) return;

      this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
      const variantAvailable = this.variant && this.variant.available;
      
      // Check B2B status (data-b2b on the picker, hidden input as fallback)
      const userHasB2bTag = this.isB2BCustomer();
      
      // Check if variant is out of stock (inventory = 0 and inventory tracking enabled)
      let isOutOfStock = false;
      if (this.variant) {
        // Get inventory data from formatted JSON (which has inventory_quantity) or from variant object
        // Note: formatted JSON uses variant ID as a string key, so convert to string for lookup
        const variantIdStr = String(this.variant.id);
        const formattedVariant = this.data.formatted && this.data.formatted[variantIdStr];
        const inventoryManagement = (formattedVariant && formattedVariant.inventoryManagement) || this.variant.inventory_management;
        const inventoryQuantity = (formattedVariant && formattedVariant.inventoryQuantity !== undefined) 
          ? formattedVariant.inventoryQuantity 
          : (this.variant.inventory_quantity !== undefined ? this.variant.inventory_quantity : undefined);
        
        // If inventory tracking is enabled (not null/undefined/blank) and quantity is 0 or less
        if (inventoryManagement && inventoryQuantity !== undefined && inventoryQuantity !== null) {
          if (inventoryQuantity <= 0) {
            isOutOfStock = true;
          }
        }
      }
      
      // Determine if button should be disabled
      // B2B users can always buy (continue selling regardless of stock)
      // Disable if: variant not selected OR (not available AND user doesn't have B2B tag)
      // Commande en rupture : une variante explicitement autorisee reste achetable meme a
      // stock zero. Sans ce test, ce JS remettait « Epuise » sur une variante commandable
      // — le serveur rendait la bonne page et le changement de pointure l'ecrasait.
      const backorder = this.getBackorderInfo();

      let shouldDisable = this.preSelection;
      if (!shouldDisable && !userHasB2bTag && !(backorder && backorder.eligible)) {
        if (isOutOfStock || !variantAvailable) {
          shouldDisable = true;
        }
      }

      this.addBtn.disabled = shouldDisable;
      // Rudy A. 2026-09-10: B2B customers order regardless of stock and never see the
      // backorder notice (theme rule: no stock messages for B2B).
      this.updateBackorderNotice(backorder, isOutOfStock && !userHasB2bTag);

      if (this.preSelection) {
        this.addBtn.textContent = theme.strings.noSelectedVariant;
      } else if (userHasB2bTag) {
        // B2B users always see "Add to cart" — continue selling regardless of stock
        this.addBtn.textContent = this.addBtn.dataset.addToCartText;
      } else {
        if ((isOutOfStock || !variantAvailable) && backorder && backorder.eligible) {
          this.addBtn.textContent = (theme.strings.backorderAddToCart || this.addBtn.dataset.addToCartText);
        } else if (isOutOfStock || !variantAvailable) {
          this.addBtn.textContent = this.variant ? theme.strings.noStock : theme.strings.noVariant;
        } else {
          this.addBtn.textContent = this.addBtn.dataset.addToCartText;
        }
      }
    }

    /**
     * Checks if a variant is actually available considering inventory and B2B status.
     * @param {object} variant - Variant object.
     * @param {object} formattedData - Formatted variant data.
     * @param {boolean} userHasB2bTag - Does the user have B2B tag?
     * @returns {boolean} - Is the variant available?
     */
    static isVariantActuallyAvailable(variant, formattedData, userHasB2bTag) {
      // If user has B2B tag, always available (continue selling regardless of stock)
      if (userHasB2bTag) {
        return true;
      }

      // For non-B2B users, check inventory
      if (variant.available) {
        const variantIdStr = String(variant.id);
        const formattedVariant = formattedData && formattedData[variantIdStr];
        const inventoryManagement = (formattedVariant && formattedVariant.inventoryManagement) || variant.inventory_management;
        const inventoryQuantity = (formattedVariant && formattedVariant.inventoryQuantity !== undefined) 
          ? formattedVariant.inventoryQuantity 
          : (variant.inventory_quantity !== undefined ? variant.inventory_quantity : undefined);
        
        // If inventory tracking is enabled and quantity is 0 or less, mark as unavailable
        if (inventoryManagement && inventoryQuantity !== undefined && inventoryQuantity !== null) {
          if (inventoryQuantity <= 0) {
            return false; // Out of stock - should show crossed styling
          }
        }
      }

      return variant.available;
    }

    /**
     * Updates the availability status of an option.
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     */
    static updateOptionAvailability(optionEl, exists, available) {
      const el = optionEl;
      const unavailableText = exists ? theme.strings.noStock : theme.strings.noVariant;
      el.classList.toggle('is-unavailable', !available);
      el.classList.toggle('is-nonexistent', !exists);

      if (optionEl.classList.contains('custom-select__option')) {
        const em = el.querySelector('em');

        if (em) {
          em.hidden = available;
        }

        if (!available) {
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!available) {
        el.nextElementSibling.title = unavailableText;
      } else {
        el.nextElementSibling.removeAttribute('title');
      }
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.showAvailability === 'false') return;
      const { availabilityMethod } = this.dataset; // 'downward' or 'selection'
      let currVariant = this.variant;

      if (!this.variant) {
        currVariant = { options: this.selectedOptions };
      }

      // Get B2B status (data-b2b on the picker, product form hidden input as fallback)
      this.productForm = this.productForm || this.section.querySelector('.js-product-form-main');
      const userHasB2bTag = this.isB2BCustomer();

      if (availabilityMethod === 'selection') {
        // Flag all options as unavailable
        this.querySelectorAll('.js-option').forEach((optionEl) => {
          VariantPicker.updateOptionAvailability(optionEl, false, false);
        });

        // Flag selector options as available or sold out, depending on the variant availability
        this.optionSelectors.forEach((selector, selectorIndex) => {
          this.data.product.variants.forEach((variant) => {
            let matchCount = 0;

            variant.options.forEach((option, optionIndex) => {
              if (option === currVariant.options[optionIndex] && optionIndex !== selectorIndex) {
                matchCount += 1;
              }
            });

            if (matchCount === currVariant.options.length - 1) {
              const options = selector.querySelectorAll('.js-option');
              const optionEl = Array.from(options).find((opt) => {
                if (selector.dataset.selectorType === 'dropdown') {
                  return opt.dataset.value === variant.options[selectorIndex];
                }
                return opt.value === variant.options[selectorIndex];
              });

              if (optionEl) {
                const isActuallyAvailable = VariantPicker.isVariantActuallyAvailable(
                  variant,
                  this.data.formatted,
                  userHasB2bTag
                );
                VariantPicker.updateOptionAvailability(optionEl, true, isActuallyAvailable);
              }
            }
          });
        });
      } else {
        this.optionSelectors.forEach((selector, selectorIndex) => {
          const options = selector.querySelectorAll('.js-option:not([data-value=""])');
          options.forEach((option) => {
            const optionValue = selector.dataset.selectorType === 'dropdown' ? option.dataset.value : option.value;
            // any available variants with previous options and this one locked in?
            let variantsExist = false;
            let variantsAvailable = false;
            this.data.product.variants.forEach((v) => {
              let matches = 0;
              for (let i = 0; i < selectorIndex; i += 1) {
                if (v.options[i] === this.selectedOptions[i] || this.selectedOptions[i] === null) {
                  matches += 1;
                }
              }
              if (v.options[selectorIndex] === optionValue && matches === selectorIndex) {
                variantsExist = true;
                const isActuallyAvailable = VariantPicker.isVariantActuallyAvailable(
                  v,
                  this.data.formatted,
                  userHasB2bTag
                );
                if (isActuallyAvailable) {
                  variantsAvailable = true;
                }
              }
            });
            VariantPicker.updateOptionAvailability(option, variantsExist, variantsAvailable);
          });
        });
      }

      this.reselectNonexistent();
    }

    /**
     * Rudy A. 2026-09-11: a value that does not exist for the chosen combination (e.g. size 3
     * in Tan) is hidden by CSS (.is-nonexistent). If the checked option just became
     * nonexistent, move the selection to the first existing option (available first) so the
     * page never sits on "Indisponible" for a variant that simply does not exist.
     */
    reselectNonexistent() {
      if (this.reselecting) return;
      const selectors = Array.from(this.optionSelectors || []);
      // The colour (first selector) is always selectable: its tooltip stays the colour name.
      if (selectors[0] && selectors[0].dataset.selectorType !== 'dropdown') {
        selectors[0].querySelectorAll('.js-option.is-nonexistent').forEach((radio) => {
          const label = radio.nextElementSibling;
          if (label && label.tagName === 'LABEL') label.title = radio.value;
        });
      }
      for (let i = 1; i < selectors.length; i += 1) { // directional: never touch the first selector (colour)
        const selector = selectors[i];
        if (selector.dataset.selectorType === 'dropdown') continue;
        const checked = selector.querySelector('.js-option:checked');
        if (!checked || !checked.classList.contains('is-nonexistent')) continue;
        const candidates = Array.from(selector.querySelectorAll('.js-option:not(.is-nonexistent)'));
        const next = candidates.find((o) => !o.classList.contains('is-unavailable')) || candidates[0];
        if (!next) continue;
        this.reselecting = true;
        try {
          next.checked = true;
          next.dispatchEvent(new Event('change', { bubbles: true }));
        } finally {
          this.reselecting = false;
        }
        return;
      }
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id];

        if (this.variant.inventory_management && inventory === 'none') {
          const backorderProdEl = this.backorder.querySelector('.backorder__product');
          const prodTitleEl = this.section.querySelector('.product-title');
          const variantTitle = this.variant.title.includes('Default')
            ? ''
            : ` - ${this.variant.title}`;

          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the color option label text.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      const selector = evt.target.closest('.option-selector');
      if (selector.dataset.selectorType === 'dropdown') return;

      const colorText = selector.querySelector('.js-color-text');
      if (!colorText) return;

      colorText.textContent = evt.target.nextElementSibling.querySelector('.js-value').textContent;
    }

    /**
     * Updates the product media.
     */
    updateMedia() {
      if (!this.variant.featured_media) return;

      if (this.section.matches('quick-add-drawer')) {
        this.section.updateMedia(this.variant.featured_media.id);
      } else {
        this.mediaGallery = this.mediaGallery || this.section.querySelector('media-gallery');
        if (!this.mediaGallery) return;

        const variantMedia = this.mediaGallery.querySelector(
          `[data-media-id="${this.variant.featured_media.id}"]`
        );
        this.mediaGallery.setActiveMedia(variantMedia, true, true);
      }
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability =
        this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    /**
     * Updates the price.
     */
    updatePrice() {
      this.price = this.price || this.section.querySelector('.product-info__price > .price');
      // For B2B, the price container is a .b2b-injected-price with minimal structure.
      if (!this.price) {
        const b2bPrice = this.section.querySelector('.product-info__price .b2b-injected-price');
        if (b2bPrice) return; // B2B price managed by b2b-pdp-price.js
        return;
      }

      let { variant } = this;
      if (this.preSelection) {
        variant = this.data.product.variants[0];
        for (let i = 1; i < this.data.product.variants.length; i += 1) {
          if (this.data.product.variants[i].price < variant.price) variant = this.data.product.variants[i];
        }
      }

      if (this.variant) {
        const priceCurrentEl = this.price.querySelector('.price__current');
        const priceWasEl = this.price.querySelector('.price__was');
        const unitPriceEl = this.price.querySelector('.unit-price');

        // Guard: B2B injected price has .price__current but no .js-label
        const currentLabel = priceCurrentEl && priceCurrentEl.querySelector('.js-label');
        const currentValue = priceCurrentEl && priceCurrentEl.querySelector('.js-value');

        if (variant.compare_at_price > variant.price) {
          if (currentLabel) currentLabel.textContent = theme.strings.salePrice;
          if (currentValue) currentValue.innerHTML = this.data.formatted[variant.id].price;
          if (priceWasEl) {
            const wasLabel = priceWasEl.querySelector('.js-label');
            const wasValue = priceWasEl.querySelector('.js-value');
            if (wasLabel) wasLabel.textContent = theme.strings.regularPrice;
            if (wasValue) wasValue.innerHTML = this.data.formatted[variant.id].compareAtPrice || '';
          }
        } else {
          if (currentLabel) currentLabel.textContent = theme.strings.regularPrice;
          if (currentValue) currentValue.innerHTML = this.data.formatted[variant.id].price;
          if (priceWasEl) {
            const wasLabel = priceWasEl.querySelector('.js-label');
            const wasValue = priceWasEl.querySelector('.js-value');
            if (wasLabel) wasLabel.textContent = '';
            if (wasValue) wasValue.innerHTML = '';
          }
        }

        if (this.variant.unit_price_measurement && unitPriceEl) {
          const valueEl = this.price.querySelector('.unit-price__price');
          const unitEl = this.price.querySelector('.unit-price__unit');
          const value = this.variant.unit_price_measurement.reference_value;
          const unit = this.variant.unit_price_measurement.reference_unit;
          if (valueEl) valueEl.innerHTML = this.data.formatted[this.variant.id].unitPrice;
          if (unitEl) unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
        }

        if (unitPriceEl) unitPriceEl.hidden = !this.variant.unit_price_measurement;
        this.price.classList.toggle('price--on-sale', this.variant.compare_at_price > this.variant.price);
        this.price.classList.toggle('price--sold-out', !this.variant.available && !this.preSelection);
      }

      const priceDefault = this.price.querySelector('.price__default');
      const priceNoVariant = this.price.querySelector('.price__no-variant');
      if (priceDefault) priceDefault.hidden = !this.variant && !this.preSelection;
      if (priceNoVariant) priceNoVariant.hidden = this.variant || this.preSelection;
      const from = this.price.querySelector('.price__from');
      if (from) {
        from.hidden = !this.preSelection;
      }
    }

    /**
     * Updates the weight.
     */
    updateWeight() {
      this.weights = this.weights || this.section.querySelectorAll('.product-info__weight');
      if (this.weights.length === 0) return;

      const weightAvailable = this.variant && this.variant.weight > 0;
      this.weights.forEach((weight) => {
        weight.textContent = weightAvailable ? this.data.formatted[this.variant.id].weight : '';
        weight.hidden = !weightAvailable;
      });
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcodes = this.barcodes || this.section.querySelectorAll('.product-info__barcode-value');
      if (this.barcodes.length === 0) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcodes.forEach((barcode) => {
        barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
        barcode.parentNode.hidden = !barcodeAvailable;
      });
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector('.product-sku__value');
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : '';
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the quantity input max attribute based on inventory for non-B2B customers.
     */
    updateQuantityMax() {
      this.productForm = this.productForm || this.section.querySelector('.js-product-form-main');
      if (!this.productForm) return;

      const quantityInput = this.productForm.querySelector('.qty-input__input[name="quantity"]');
      if (!quantityInput) return;

      const maxMessage = this.productForm.querySelector('.qty-max-message');
      
      // Check B2B status (data-b2b on the picker, hidden input as fallback)
      const userHasB2bTag = this.isB2BCustomer();

      // For B2B customers, allow unlimited quantity (remove max)
      if (userHasB2bTag) {
        quantityInput.removeAttribute('max');
        if (maxMessage) maxMessage.style.display = 'none';
        return;
      }

      // For non-B2B customers, limit to inventory quantity
      if (this.variant) {
        const variantIdStr = String(this.variant.id);
        const formattedVariant = this.data.formatted && this.data.formatted[variantIdStr];
        const inventoryManagement = (formattedVariant && formattedVariant.inventoryManagement) || this.variant.inventory_management;
        const inventoryQuantity = (formattedVariant && formattedVariant.inventoryQuantity !== undefined) 
          ? formattedVariant.inventoryQuantity 
          : (this.variant.inventory_quantity !== undefined ? this.variant.inventory_quantity : undefined);

        // If inventory tracking is enabled and we have a quantity, set max
        if (inventoryManagement && inventoryQuantity !== undefined && inventoryQuantity !== null) {
          if (inventoryQuantity > 0) {
            quantityInput.setAttribute('max', inventoryQuantity);
            // Show/hide max message based on current quantity
            this.updateMaxMessage(quantityInput, maxMessage, inventoryQuantity);
          } else {
            // Rudy A. 2026-09-11: out of stock. If the variant can be ordered on backorder,
            // the quantity is not capped (a max of 0 made the theme show "Quantité maximale
            // atteinte" and blocked the quantity on an orderable item). Otherwise max = 0.
            const backorderInfo = this.getBackorderInfo && this.getBackorderInfo();
            if (backorderInfo && backorderInfo.eligible) {
              quantityInput.removeAttribute('max');
            } else {
              quantityInput.setAttribute('max', '0');
            }
            if (maxMessage) maxMessage.style.display = 'none';
          }
        } else {
          // No inventory tracking, remove max
          quantityInput.removeAttribute('max');
          if (maxMessage) maxMessage.style.display = 'none';
        }
      } else {
        // No variant selected, remove max
        quantityInput.removeAttribute('max');
        if (maxMessage) maxMessage.style.display = 'none';
      }
    }

    /**
     * Updates the max quantity message visibility based on current quantity.
     * @param {Element} quantityInput - The quantity input element.
     * @param {Element} maxMessage - The max message element.
     * @param {number} maxQuantity - The maximum allowed quantity.
     */
    updateMaxMessage(quantityInput, maxMessage, maxQuantity) {
      if (!maxMessage) return;
      
      const currentQuantity = parseInt(quantityInput.value, 10) || 0;
      const maxAttr = quantityInput.getAttribute('max');
      const effectiveMax = maxAttr ? parseInt(maxAttr, 10) : maxQuantity;
      
      // Show message if quantity equals max and user is not B2B
      if (effectiveMax && currentQuantity >= effectiveMax && effectiveMax > 0) {
        maxMessage.style.display = 'block';
      } else {
        maxMessage.style.display = 'none';
      }
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.variant.id}`);
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      // HTML ignores nested <form> tags — all inputs belong to the outer form.
      // Update ALL input[name="id"] in the section so FormData always reads the correct variant.
      const allIdInputs = this.section.querySelectorAll('input[name="id"]');
      allIdInputs.forEach((input) => {
        input.value = this.variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /**
     * Gets the selected option element from each selector.
     * @returns {Array}
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selectedText = selector.querySelector('.custom-select__btn').textContent.trim();
          selectedOptions.push(selectedText === this.dataset.placeholderText ? null : selectedText);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptions.push(selected ? selected.value : null);
        }
      });

      return selectedOptions;
    }

    /**
     * Gets the product data.
     * @returns {?object}
     */
    getProductData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Get selected variant data.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      const selectedOptions = this.getSelectedOptions();
      return this.data.product.variants.find(
        (v) => v.options.every((val, index) => val === selectedOptions[index])
      );
    }
  }

  customElements.define('variant-picker', VariantPicker);
}
